import type { TablebaseCategory } from "@chessla/shared";

export const LICHESS_TABLEBASE_BUILD = "lila-tablebase@5eaf827193e69485c981fc10dcb7747198451964";

export interface BrowserTablebaseMove {
  uci: string;
  san: string;
  category: string;
  dtz: number | null;
  dtm: number | null;
}

export interface BrowserTablebaseProbe {
  category: TablebaseCategory;
  dtz: number | null;
  preciseDtz: number | null;
  dtm: number | null;
  checkmate: boolean;
  stalemate: boolean;
  insufficientMaterial: boolean;
  moves: BrowserTablebaseMove[];
}

export interface BrowserTablebaseClient {
  probe(fen: string, signal: AbortSignal): Promise<BrowserTablebaseProbe>;
}

const categories = new Set<TablebaseCategory>([
  "win",
  "syzygy-win",
  "cursed-win",
  "draw",
  "blessed-loss",
  "syzygy-loss",
  "loss",
]);
const nullableInteger = (value: unknown): value is number | null =>
  value === null || (typeof value === "number" && Number.isInteger(value));
const unavailable = (): Error => new Error("Nikola exact endgame evidence is unavailable right now.");

export class LichessTablebaseClient implements BrowserTablebaseClient {
  constructor(private readonly request: typeof fetch = fetch) {}

  async probe(fen: string, signal: AbortSignal): Promise<BrowserTablebaseProbe> {
    const url = new URL("https://tablebase.lichess.org/standard");
    url.searchParams.set("fen", fen);
    let response: Response;
    try {
      response = await this.request.call(globalThis, url, {
        method: "GET",
        signal,
        headers: { Accept: "application/json" },
      });
    } catch (error) {
      if (signal.aborted) throw error;
      throw unavailable();
    }
    if (!response.ok) throw unavailable();

    const raw: unknown = await response.json().catch(() => null);
    if (!raw || typeof raw !== "object") throw unavailable();
    const value = raw as Record<string, unknown>;
    if (
      typeof value.category !== "string" ||
      !categories.has(value.category as TablebaseCategory) ||
      !nullableInteger(value.dtz) ||
      !nullableInteger(value.precise_dtz) ||
      !nullableInteger(value.dtm) ||
      typeof value.checkmate !== "boolean" ||
      typeof value.stalemate !== "boolean" ||
      typeof value.insufficient_material !== "boolean" ||
      !Array.isArray(value.moves)
    )
      throw unavailable();

    const moves = value.moves.slice(0, 128).map((entry) => {
      if (!entry || typeof entry !== "object") throw unavailable();
      const move = entry as Record<string, unknown>;
      if (
        typeof move.uci !== "string" ||
        typeof move.san !== "string" ||
        typeof move.category !== "string" ||
        !nullableInteger(move.dtz) ||
        !nullableInteger(move.dtm)
      )
        throw unavailable();
      return { uci: move.uci, san: move.san, category: move.category, dtz: move.dtz, dtm: move.dtm };
    });

    return {
      category: value.category as TablebaseCategory,
      dtz: value.dtz,
      preciseDtz: value.precise_dtz,
      dtm: value.dtm,
      checkmate: value.checkmate,
      stalemate: value.stalemate,
      insufficientMaterial: value.insufficient_material,
      moves,
    };
  }
}
